import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const CATEGORIES = ["Todos", "Corrida", "F1", "Avião", "Montanha Russa", "Eventos"];

export default function Gallery({ items = [] }) {
  const [cat, setCat] = useState("Todos");
  const [active, setActive] = useState(null);

  const filtered = useMemo(
    () => (cat === "Todos" ? items : items.filter((i) => i.category === cat)),
    [cat, items]
  );

  const spanCls = (span) => {
    if (span === "tall") return "row-span-2";
    if (span === "wide") return "md:col-span-2";
    return "";
  };

  const openIdx = (item) => setActive(filtered.findIndex((f) => f.id === item.id));
  const close = () => setActive(null);
  const prev = () => setActive((v) => (v > 0 ? v - 1 : filtered.length - 1));
  const next = () => setActive((v) => (v < filtered.length - 1 ? v + 1 : 0));

  return (
    <section id="galeria" className="relative py-28 md:py-40 bg-[#050505]" data-testid="gallery-section">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-end justify-between gap-8 flex-wrap mb-10">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#008CFF]" />
              <span className="overline">Momentos capturados</span>
            </div>
            <h2 className="font-display font-black text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              <span className="text-white">GALERIA</span>{" "}
              <span className="neon-blue">SIMEXP</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`rounded-full px-4 py-2 text-xs uppercase tracking-widest transition-colors border ${
                  cat === c
                    ? "bg-[#E31B23] text-white border-[#E31B23]"
                    : "border-white/10 text-neutral-300 hover:border-white/30"
                }`}
                data-testid={`gallery-filter-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[220px] gap-3">
          {filtered.map((g, i) => (
            <motion.button
              layout
              key={g.id}
              onClick={() => openIdx(g)}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.03 }}
              className={`group relative overflow-hidden rounded-xl border border-white/5 ${spanCls(g.span)}`}
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={g.image_url}
                alt={g.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
              <div className="absolute bottom-3 left-3 right-3 text-left">
                <div className="overline text-neutral-300 text-[10px]">{g.category}</div>
                <div className="font-display font-bold text-white text-sm md:text-base leading-tight">{g.title}</div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && filtered[active] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[500] flex items-center justify-center p-4 md:p-10"
            data-testid="gallery-lightbox"
          >
            <div className="absolute inset-0 bg-black/95 backdrop-blur-xl" onClick={close} />
            <button className="absolute top-6 right-6 p-2 rounded-full border border-white/10 z-10" onClick={close} data-testid="lightbox-close">
              <X className="w-5 h-5" />
            </button>
            <button className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-3 rounded-full border border-white/10 z-10" onClick={prev} data-testid="lightbox-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-3 rounded-full border border-white/10 z-10" onClick={next} data-testid="lightbox-next">
              <ChevronRight className="w-5 h-5" />
            </button>
            <motion.img
              key={filtered[active].id}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              src={filtered[active].image_url}
              alt={filtered[active].title}
              className="relative max-h-[85vh] max-w-[90vw] object-contain rounded-xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
